// src/pages/purchase/ConfirmSiteReceipt.js
// Direct Site Purchase — Stage 1: Site Engineer confirms received qty.
// No rate field, no inventory/financial write — pure ground-truth
// observation (same pattern as Subcontractor Work's field-log stage).

import { useEffect, useState } from "react";
import { db } from "../../firebase";
import { doc, getDoc, collection, getDocs, query, where } from "firebase/firestore";
import { useParams, useNavigate } from "react-router-dom";
import { confirmSiteReceipt } from "../../services/directSitePurchaseService";

const userData = () => JSON.parse(localStorage.getItem("user") || "{}");

export default function ConfirmSiteReceipt() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const me       = userData();

  const [po,         setPo]         = useState(null);
  const [items,      setItems]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (id) fetchPO(); }, [id]); // eslint-disable-line

  const fetchPO = async () => {
    setLoading(true);
    setError(null);
    try {
      const snap = await getDoc(doc(db, "purchase_orders", id));
      if (!snap.exists()) { setError("Purchase order not found."); setLoading(false); return; }

      const poData = { id: snap.id, ...snap.data() };

      if (poData.company_id !== me.company_id) {
        setError("Access denied."); setLoading(false); return;
      }
      if (poData.purchase_mode !== "direct_site_purchase") {
        setError("This is not a Direct Site Purchase order."); setLoading(false); return;
      }
      if (poData.grn_locked || poData.site_confirmation_status === "completed") {
        setError("This purchase has already been finalized."); setLoading(false); return;
      }
      if (poData.site_confirmation_status !== "pending_site_confirmation") {
        setError("Already confirmed — awaiting Manager finalization."); setLoading(false); return;
      }
      if (poData.po_status !== "ordered") {
        setError(`This PO is "${poData.po_status}" — it must be Approved and marked Ordered before you can confirm receipt. Contact your Manager.`);
        setLoading(false); return;
      }

      // Access check — a PO-specific pin (assigned_site_engineer_ids)
      // takes priority over the project-wide assignment when present.
      if (me.role === "site_engineer") {
        const myUid = me.uid || me.id;
        const pinned = poData.assigned_site_engineer_ids || [];

        if (pinned.length > 0) {
          if (!pinned.includes(myUid)) {
            setError("This delivery is assigned to a different Site Engineer."); setLoading(false); return;
          }
        } else if (poData.project_id) {
          const projSnap = await getDoc(doc(db, "projects", poData.project_id));
          const assigned = projSnap.exists()
            ? (projSnap.data().site_engineer_ids || []).includes(myUid)
            : false;
          if (!assigned) {
            setError("You are not assigned to this project."); setLoading(false); return;
          }
        }
      }

      setPo(poData);
      setItems(
        (poData.items || [])
          .filter(item => !item._cancelled)
          .map(item => ({
            ...item,
            confirmed_qty: Number(item.ordered_qty || 0),
          }))
      );
    } catch (err) {
      console.error("[ConfirmSiteReceipt] fetchPO error:", err);
      setError("Failed to load purchase order.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (submitting) return;

    const zeroQty = items.find(i => Number(i.confirmed_qty || 0) <= 0);
    if (zeroQty) {
      if (!window.confirm(`"${zeroQty.item_name}" has 0 quantity — it will be recorded as not delivered. Continue?`)) return;
    }

    // Removed the extra window.confirm() here — this action is non-
    // destructive (no stock/money moves yet) and a Manager reviews it
    // again before Finalize, so the button click itself is sufficient
    // confirmation. One popup (the success message below) instead of two.
    setSubmitting(true);
    try {
      const confirmed_items = items
        .filter(i => Number(i.confirmed_qty || 0) > 0)
        .map(i => ({
          line_id:      i.line_id || "",
          item_id:      i.item_id,
          item_code:    i.item_code || "",
          item_name:    i.item_name,
          category:     i.category || i.item_category || "MATERIAL",
          unit:         i.unit || "nos",
          ordered_qty:  Number(i.ordered_qty || 0),
          confirmed_qty: Number(i.confirmed_qty || 0),
          is_custom_item: !!i.is_custom_item,
        }));

      if (confirmed_items.length === 0) {
        alert("Enter at least one received quantity.");
        setSubmitting(false);
        return;
      }

      await confirmSiteReceipt({
        po_id: id,
        company_id: me.company_id,
        confirmed_items,
        confirmed_by: me.uid || me.id || "",
        confirmed_by_name: me.name || "",
      });

      alert("✅ Receipt confirmed. A Manager will finalize rate and vendor payment.");
      navigate("/my-site-confirmations", { replace: true });

    } catch (err) {
      console.error("[ConfirmSiteReceipt] error:", err);
      alert(err.message || "Failed to confirm receipt. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <p className="text-gray-500 text-sm">Loading...</p>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 p-6 max-w-2xl mx-auto">
      <button onClick={() => navigate(-1)} className="mb-4 text-sm text-slate-500 hover:text-slate-800">← Back</button>
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-700 font-medium">{error}</p>
      </div>
    </div>
  );

  if (!po) return null;

  return (
    <div className="min-h-screen bg-gray-50 p-4 max-w-2xl mx-auto">
      <button onClick={() => navigate(-1)} className="mb-4 text-sm text-slate-500 hover:text-slate-800">← Back</button>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-xl font-bold text-slate-800">Confirm Site Receipt</h2>
            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">Direct Site Purchase</span>
          </div>
          <p className="text-xs text-slate-400">
            PO: {po.po_number || id} • {po.vendor_name || ""} • {po.project_name} — {po.site_name}
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-5 text-xs text-blue-800">
          Enter the quantity actually received at site for each item. No rate needed here —
          a Manager/Owner will finalize rate and vendor payment after reviewing your confirmation.
        </div>

        <div className="space-y-3 mb-6">
          {items.map((item, i) => (
            <div key={i} className="border border-slate-200 rounded-2xl p-4 bg-white">
              <div className="flex justify-between items-center gap-3">
                <div className="flex-1">
                  <p className="font-semibold text-slate-800 text-sm">{item.item_name}</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Ordered: {Number(item.ordered_qty || 0)} {item.unit || "nos"}
                  </p>
                  {item.is_custom_item && (
                    <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full mt-1 inline-block">⚡ Custom</span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <input
                    type="number" min="0"
                    value={item.confirmed_qty ?? 0}
                    onChange={e => {
                      const u = [...items];
                      u[i].confirmed_qty = Math.max(0, Number(e.target.value));
                      setItems(u);
                    }}
                    className="w-24 border border-slate-200 rounded-xl px-2 py-1.5 text-right text-sm outline-none focus:ring-2 focus:ring-blue-300"
                  />
                  <span className="text-xs text-slate-400 w-10">{item.unit || "nos"}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full py-3 rounded-xl bg-blue-600 text-white font-semibold text-sm disabled:opacity-40 hover:bg-blue-700 transition">
          {submitting ? "Confirming..." : "✓ Confirm Received Quantities"}
        </button>
      </div>
    </div>
  );
}